import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { lineDiff } from '../../services/git/changeItems';

const KIND = {
  added: { mark: 'A', label: 'Added' },
  modified: { mark: 'M', label: 'Changed' },
  deleted: { mark: 'D', label: 'Deleted' },
  renamed: { mark: 'R', label: 'Renamed' },
};

/** "Reports → Reports renamed", plus what moved with a folder. */
function itemTitle(item) {
  if (item.kind !== 'renamed') return item.name;
  const was = item.previousName && item.previousName !== item.name ? `${item.previousName} → ${item.name}` : `${item.name} (moved)`;
  return item.inside ? `${was} · ${item.inside} item${item.inside === 1 ? '' : 's'} inside` : was;
}

/** Each changed field: its label, the old value as a − line and the new one as a + line (Git page redesign, 05.10.2026). */
function FieldList({ fields }) {
  if (!fields.length) return null;
  return (
    <ul className="git-fields">
      {fields.map((f, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={i} className={`git-field git-field--${f.change}`}>
          <span className="git-field-label">{f.label}</span>
          <span className="git-field-diff">
            {(f.change === 'removed' || f.change === 'changed') && (
              <span className="git-val git-val--old">
                <em aria-hidden>−</em>
                {f.before}
              </span>
            )}
            {(f.change === 'added' || f.change === 'changed') && (
              <span className="git-val git-val--new">
                <em aria-hidden>+</em>
                {f.after}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function RawDiff({ file }) {
  const lines = lineDiff(file?.before || '', file?.after || '');
  return (
    <pre className="git-raw" data-testid="git-raw-diff">
      {lines.map((l, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className={`git-raw-line git-raw-line--${l.op === '+' ? 'add' : l.op === '-' ? 'del' : 'ctx'}`}>
          {l.op} {l.text}
        </div>
      ))}
    </pre>
  );
}

/**
 * One changed item (Changes and History share it): A/M/D mark and name;
 * opened, its fields, the raw file diff, and whatever `actions` the caller
 * puts next to them (Discard, Restore this version).
 */
export default function GitChangeRow({ item, file, actions = null }) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState(false);
  const k = KIND[item.kind] || KIND.modified;
  return (
    <div className="git-change" data-testid="git-change">
      <button type="button" className="git-change-head" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <ChevronRight size={13} className={`git-chevron${open ? ' git-chevron--open' : ''}`} />
        <span className={`git-kind git-kind--${item.kind}`} title={k.label}>{k.mark}</span>
        <span className="git-change-name">{itemTitle(item)}</span>
        {item.fields.length > 0 && (
          <span className="git-change-count">{item.fields.length} field{item.fields.length === 1 ? '' : 's'}</span>
        )}
      </button>
      {open && (
        <div className="git-change-body">
          {item.fields.length ? <FieldList fields={item.fields} /> : <p className="git-empty">{k.label} — {item.path}</p>}
          <div className="git-change-actions">
            <button type="button" className="git-link" onClick={() => setRaw((v) => !v)}>
              {raw ? 'Hide file diff' : 'Show file diff'}
            </button>
            {actions}
          </div>
          {raw && <RawDiff file={file} />}
        </div>
      )}
    </div>
  );
}
